import dayjs from 'dayjs'

import { type PatientAppointmentType } from 'features/Schedule/pusher/type'

const ECHO_TTL_MS = 15_000
// A `pending` snapshot only suppresses events while the local mutation is in flight. Keep its TTL
// short so a stuck/failed mutation can't silently hide real Pusher events for the full 15s.
const PENDING_ECHO_TTL_MS = 4_000

type NormalizedPatient = {
  id: number
  date: string
  updated_at: string
  emr_updated_at: string | null
  roomCode: string
  start_time: string
  end_time: string
  status_code: number | undefined
  temperature: number | undefined
  consultation_staff: number | undefined
  sms_notification: string | number | undefined
  patient_id: number | undefined
  jitsi_embed: string
  jitsi_patient_link: string
}

type AppointmentEchoSnapshot =
  | { kind: 'upsert'; expiresAt: number; normalized: NormalizedPatient }
  | { kind: 'delete'; expiresAt: number }
  | { kind: 'status'; expiresAt: number; status_code: number; temperature: number }

type RoomEchoSnapshot = { expiresAt: number; roomStatusCode: number }

const appointmentEchoes = new Map<number, AppointmentEchoSnapshot>()
const roomEchoes = new Map<number, RoomEchoSnapshot>()

export type PendingMutationKind = 'upsert' | 'delete' | 'status'

export type ScheduleSlotSnapshot = {
  date: string
  start_time: string
  end_time: string
  room: string
}

export type PendingMutationSnapshot = {
  kind?: PendingMutationKind
  previous?: ScheduleSlotSnapshot
  intended?: ScheduleSlotSnapshot
}

type PendingMutation = {
  count: number
  expiresAt: number
  kind: PendingMutationKind
  previous?: ScheduleSlotSnapshot
  intended?: ScheduleSlotSnapshot
}

const pendingMutations = new Map<number, PendingMutation>()

const normalizeTimestamp = (value?: string | null): string => {
  if (!value) return ''
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.toISOString() : value
}

export const normalizeScheduleTime = (value?: string | Date | null): string => {
  if (!value) return ''
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format('HH:mm:ss') : String(value)
}

const resolveRoomCode = (
  room?: { code?: string; value?: string } | string | null,
): string => {
  if (!room) return ''
  if (typeof room === 'string') return room
  return room.code ?? room.value ?? ''
}

export const toScheduleSlotSnapshot = (data: {
  date?: string | null
  start_time?: string | Date | null
  end_time?: string | Date | null
  room?: { code?: string; value?: string } | string | null
}): ScheduleSlotSnapshot => ({
  date: data.date ?? '',
  start_time: normalizeScheduleTime(data.start_time),
  end_time: normalizeScheduleTime(data.end_time),
  room: resolveRoomCode(data.room),
})

export const scheduleSlotsMatch = (
  left: ScheduleSlotSnapshot,
  right: ScheduleSlotSnapshot,
): boolean =>
  left.date === right.date &&
  left.start_time === right.start_time &&
  left.end_time === right.end_time &&
  left.room === right.room

const normalizePatient = (data: PatientAppointmentType): NormalizedPatient => {
  const { room, patient, start_time, end_time, status, emr_updated_at, ...rest } = data

  return {
    id: rest.id,
    date: rest.date,
    updated_at: normalizeTimestamp(rest.updated_at),
    emr_updated_at: emr_updated_at ?? null,
    roomCode: room?.code ?? '',
    start_time: normalizeScheduleTime(start_time),
    end_time: normalizeScheduleTime(end_time),
    status_code: status?.code,
    temperature: rest.temperature,
    consultation_staff: rest.consultation_staff,
    sms_notification: rest.sms_notification,
    patient_id: patient?.id,
    jitsi_embed: rest.jitsi_telehealth?.doctor_embed_url ?? '',
    jitsi_patient_link: rest.jitsi_telehealth?.patient_short_link ?? '',
  }
}

const isExpired = (expiresAt: number) => Date.now() > expiresAt

const getAppointmentEcho = (appointmentId: number): AppointmentEchoSnapshot | undefined => {
  const snapshot = appointmentEchoes.get(appointmentId)
  if (!snapshot) return undefined
  if (isExpired(snapshot.expiresAt)) {
    appointmentEchoes.delete(appointmentId)
    return undefined
  }
  return snapshot
}

const getPendingMutation = (appointmentId: number): PendingMutation | undefined => {
  const current = pendingMutations.get(appointmentId)
  if (!current) return undefined
  if (isExpired(current.expiresAt) || current.count <= 0) {
    pendingMutations.delete(appointmentId)
    return undefined
  }
  return current
}

const hasMeaningfulPatientChange = (
  incoming: NormalizedPatient,
  stored: NormalizedPatient,
): boolean => {
  if (incoming.updated_at !== stored.updated_at) return true

  return (
    incoming.date !== stored.date ||
    incoming.roomCode !== stored.roomCode ||
    incoming.start_time !== stored.start_time ||
    incoming.end_time !== stored.end_time ||
    incoming.status_code !== stored.status_code ||
    incoming.temperature !== stored.temperature ||
    incoming.consultation_staff !== stored.consultation_staff ||
    incoming.sms_notification !== stored.sms_notification ||
    incoming.patient_id !== stored.patient_id ||
    incoming.emr_updated_at !== stored.emr_updated_at ||
    incoming.jitsi_embed !== stored.jitsi_embed ||
    incoming.jitsi_patient_link !== stored.jitsi_patient_link
  )
}

export const markScheduleMutationPending = (
  appointmentId: number,
  snapshot: PendingMutationSnapshot = {},
) => {
  const current = getPendingMutation(appointmentId)
  pendingMutations.set(appointmentId, {
    count: (current?.count ?? 0) + 1,
    expiresAt: Date.now() + PENDING_ECHO_TTL_MS,
    kind: snapshot.kind ?? current?.kind ?? 'upsert',
    previous: current?.previous ?? snapshot.previous,
    intended: snapshot.intended ?? current?.intended,
  })
}

export const settleScheduleMutationPending = (appointmentId: number) => {
  const current = pendingMutations.get(appointmentId)
  if (!current) return
  if (current.count <= 1 || isExpired(current.expiresAt)) {
    pendingMutations.delete(appointmentId)
    return
  }
  pendingMutations.set(appointmentId, { ...current, count: current.count - 1 })
}

export const clearScheduleMutationEcho = (appointmentId: number) => {
  appointmentEchoes.delete(appointmentId)
}

export const hasPendingScheduleMutation = (appointmentId: number): boolean =>
  getPendingMutation(appointmentId) != null

export type PendingPatientPayloadKind = 'none' | 'stale' | 'intended' | 'remote'

export const classifyPendingPatientUpsert = (data: {
  id: number
  date?: string | null
  start_time?: string | Date | null
  end_time?: string | Date | null
  room?: { code?: string; value?: string } | string | null
}): PendingPatientPayloadKind => {
  const pending = getPendingMutation(data.id)
  if (!pending) return 'none'
  if (pending.kind !== 'upsert') return 'remote'

  const incoming = toScheduleSlotSnapshot(data)
  if (pending.previous && scheduleSlotsMatch(incoming, pending.previous)) return 'stale'
  if (pending.intended && scheduleSlotsMatch(incoming, pending.intended)) return 'intended'
  if (pending.previous || pending.intended) return 'remote'
  return 'remote'
}

export const resetSchedulePusherEcho = () => {
  appointmentEchoes.clear()
  roomEchoes.clear()
  pendingMutations.clear()
}

export const recordScheduleMutationUpsert = (data: PatientAppointmentType) => {
  appointmentEchoes.set(data.id, {
    kind: 'upsert',
    expiresAt: Date.now() + ECHO_TTL_MS,
    normalized: normalizePatient(data),
  })
}

export const recordScheduleMutationDelete = (appointmentId: number) => {
  appointmentEchoes.set(appointmentId, {
    kind: 'delete',
    expiresAt: Date.now() + ECHO_TTL_MS,
  })
}

export const recordScheduleMutationStatus = (
  appointmentId: number,
  status_code: number,
  temperature: number,
) => {
  appointmentEchoes.set(appointmentId, {
    kind: 'status',
    expiresAt: Date.now() + ECHO_TTL_MS,
    status_code,
    temperature,
  })
}

export const recordScheduleMutationRoomStatus = (roomId: number, roomStatusCode: number) => {
  roomEchoes.set(roomId, {
    expiresAt: Date.now() + ECHO_TTL_MS,
    roomStatusCode,
  })
}

export const isRedundantPusherPatientUpsert = (data: PatientAppointmentType): boolean => {
  const pendingPayload = classifyPendingPatientUpsert(data)
  if (pendingPayload === 'stale' || pendingPayload === 'intended') return true

  const snapshot = getAppointmentEcho(data.id)
  if (!snapshot) return false
  if (snapshot.kind === 'delete') return true
  if (snapshot.kind !== 'upsert') return false

  const incoming = normalizePatient(data)
  const stored = snapshot.normalized

  return !hasMeaningfulPatientChange(incoming, stored)
}

export const isRedundantPusherPatientDelete = (appointmentId: number): boolean => {
  const pending = getPendingMutation(appointmentId)
  if (pending?.kind === 'delete') return true
  return getAppointmentEcho(appointmentId)?.kind === 'delete'
}

export const isRedundantPusherPatientStatus = (
  appointmentId: number,
  status_code: number,
  temperature: number,
): boolean => {
  const snapshot = getAppointmentEcho(appointmentId)
  if (!snapshot) return false

  if (snapshot.kind === 'status') {
    return snapshot.status_code === status_code && snapshot.temperature === temperature
  }

  if (snapshot.kind === 'upsert') {
    return (
      snapshot.normalized.status_code === status_code &&
      snapshot.normalized.temperature === temperature
    )
  }

  return false
}

export const isRedundantPusherRoomStatus = (roomId: number, roomStatusCode: number): boolean => {
  const snapshot = roomEchoes.get(roomId)
  if (!snapshot) return false
  if (isExpired(snapshot.expiresAt)) {
    roomEchoes.delete(roomId)
    return false
  }
  return snapshot.roomStatusCode === roomStatusCode
}
